import React, { useState } from "react";
import { useAppDispatch } from "../../../State/Store";
import {
  Button,
  CircularProgress,
  TextField,
  IconButton,
  InputAdornment,
  Typography,
  Box,
  Alert,
  Collapse,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import { Visibility, VisibilityOff, Email, Lock } from "@mui/icons-material";
import { useFormik } from "formik";
import * as Yup from "yup";
import { sellerLogin } from "../../../State/seller/sellerAuthSlice";
import { useNavigate } from "react-router-dom";
import api from "../../../config/Api";

const SellerLoginForm = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  // Reset Password Dialog State
  const [openResetDialog, setOpenResetDialog] = useState(false);
  const [resetEmail, setResetEmail] = useState("");
  const [resetMessage, setResetMessage] = useState<{ text: string; severity: "success" | "error" } | null>(null);
  const [resetLoading, setResetLoading] = useState(false);

  const formik = useFormik({
    initialValues: {
      email: "",
      password: "",
    },
    validationSchema: Yup.object({
      email: Yup.string()
        .email("Invalid email format")
        .required("Email or Username is required"),
      password: Yup.string().required("Password is required"),
    }),
    onSubmit: async (values) => {
      setLoading(true);
      setErrorMessage(null);

      const formattedPayload = {
        email: values.email.trim(),
        password: values.password,
      };

      try {
        const resultAction = await dispatch(
          sellerLogin({ loginRequest: formattedPayload, navigate })
        );

        if (sellerLogin.rejected.match(resultAction)) {
          const errorPayload = resultAction.payload as any;
          const msg =
            typeof errorPayload === "string"
              ? errorPayload
              : errorPayload?.message || "Login failed. Please check your details.";
          setErrorMessage(msg);
        }
      } catch (err: any) {
        setErrorMessage(err.message || "An unexpected error occurred.");
      } finally {
        setLoading(false);
      }
    },
  });

  const handleInitiatePasswordReset = async () => {
    if (!resetEmail || !resetEmail.includes("@")) {
      setResetMessage({ text: "Please enter a valid seller email address.", severity: "error" });
      return;
    }

    setResetLoading(true);
    setResetMessage(null);

    try {
      await api.post("/sellers/reset-password", { email: resetEmail.trim() });
      setResetMessage({
        text: "A new temporary 10-character password has been generated and emailed to you.",
        severity: "success",
      });
    } catch (error: any) {
      setResetMessage({
        text: error.response?.data?.message || "Failed to reset password. Please ensure the email is registered.",
        severity: "error",
      });
    } finally {
      setResetLoading(false);
    }
  };

  return (
    <Box className="w-full space-y-6">
      <div>
        <Typography
          variant="h4"
          className="font-extrabold text-slate-900 tracking-tight text-2xl sm:text-3xl"
        >
          Sign In
        </Typography>
        <Typography variant="body2" className="text-slate-500 mt-1">
          Enter your credentials to access your seller store dashboard.
        </Typography>
      </div>

      <Collapse in={Boolean(errorMessage)}>
        <Alert
          severity="error"
          onClose={() => setErrorMessage(null)}
          sx={{ borderRadius: "12px", fontSize: "0.875rem" }}
        >
          {errorMessage}
        </Alert>
      </Collapse>

      <form onSubmit={formik.handleSubmit} className="space-y-4">
        <TextField
          fullWidth
          id="email"
          name="email"
          label="Email Address"
          placeholder="seller@example.com"
          variant="outlined"
          value={formik.values.email}
          onChange={(e) => {
            setErrorMessage(null);
            formik.handleChange(e);
          }}
          onBlur={formik.handleBlur}
          error={formik.touched.email && Boolean(formik.errors.email)}
          helperText={formik.touched.email && formik.errors.email}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Email className="text-slate-400" fontSize="small" />
              </InputAdornment>
            ),
          }}
          sx={{
            "& .MuiOutlinedInput-root": {
              borderRadius: "12px",
            },
          }}
        />

        <TextField
          fullWidth
          id="password"
          name="password"
          type={showPassword ? "text" : "password"}
          label="Password"
          placeholder="••••••••"
          variant="outlined"
          value={formik.values.password}
          onChange={(e) => {
            setErrorMessage(null);
            formik.handleChange(e);
          }}
          onBlur={formik.handleBlur}
          error={formik.touched.password && Boolean(formik.errors.password)}
          helperText={formik.touched.password && formik.errors.password}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Lock className="text-slate-400" fontSize="small" />
              </InputAdornment>
            ),
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  onClick={() => setShowPassword((prev) => !prev)}
                  edge="end"
                  aria-label="toggle password visibility"
                >
                  {showPassword ? (
                    <VisibilityOff fontSize="small" />
                  ) : (
                    <Visibility fontSize="small" />
                  )}
                </IconButton>
              </InputAdornment>
            ),
          }}
          sx={{
            "& .MuiOutlinedInput-root": {
              borderRadius: "12px",
            },
          }}
        />

        <div className="flex justify-end">
          <button
            type="button"
            onClick={() => {
              setResetMessage(null);
              setResetEmail("");
              setOpenResetDialog(true);
            }}
            className="text-xs text-teal-600 hover:underline font-semibold"
          >
            Forgot Password?
          </button>
        </div>

        <Button
          type="submit"
          fullWidth
          variant="contained"
          disabled={loading}
          sx={{
            py: "12px",
            fontSize: "1rem",
            fontWeight: 700,
            textTransform: "none",
            borderRadius: "12px",
            backgroundColor: "#0d9488",
            "&:hover": {
              backgroundColor: "#0f766e",
            },
          }}
        >
          {loading ? (
            <CircularProgress size={24} color="inherit" />
          ) : (
            "Sign In"
          )}
        </Button>
      </form>

      {/* Password Reset Modal Dialog */}
      <Dialog open={openResetDialog} onClose={() => setOpenResetDialog(false)} maxWidth="xs" fullWidth>
        <DialogTitle className="font-bold">Reset Seller Password</DialogTitle>
        <DialogContent className="space-y-4 pt-2">
          <Typography variant="body2" className="text-slate-500">
            Enter your registered seller email address to generate and send a new temporary 10-character password.
          </Typography>
          {resetMessage && (
            <Alert severity={resetMessage.severity} sx={{ fontSize: "0.85rem" }}>
              {resetMessage.text}
            </Alert>
          )}
          <TextField
            fullWidth
            label="Seller Email Address"
            type="email"
            size="small"
            value={resetEmail}
            onChange={(e) => setResetEmail(e.target.value)}
          />
        </DialogContent>
        <DialogActions className="p-4">
          <Button onClick={() => setOpenResetDialog(false)} color="inherit" size="small">
            Cancel
          </Button>
          <Button
            onClick={handleInitiatePasswordReset}
            variant="contained"
            disabled={resetLoading}
            size="small"
            sx={{ backgroundColor: "#0d9488", "&:hover": { backgroundColor: "#0f766e" } }}
          >
            {resetLoading ? <CircularProgress size={20} color="inherit" /> : "Reset Password"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SellerLoginForm;